/**
 * Data-quality assessment (spec sections 22 & 34).
 *
 * Grades a product on how much of what the value engine needs is actually
 * present, and collects the specific gaps so the UI can name them instead of
 * showing a vague "incomplete" badge.
 */

import type { DataQuality, DataQualityGrade, Product } from '@/types/product';
import { hasAnyNutrient } from '@/lib/nutrition/normalization';

/** Fields required for a full value analysis, with their relative weight. */
const REQUIRED = [
  { key: 'price', label: 'Price', weight: 2 },
  { key: 'quantity', label: 'Package size', weight: 2 },
  { key: 'energy', label: 'Calories', weight: 2 },
  { key: 'protein', label: 'Protein', weight: 2 },
  { key: 'carbohydrate', label: 'Carbohydrate', weight: 1 },
  { key: 'fat', label: 'Fat', weight: 1 },
  { key: 'store', label: 'Store', weight: 1 },
] as const;

/** Data older than this is flagged stale in the UI. */
export const STALENESS_THRESHOLD_MS = 1000 * 60 * 60 * 24 * 7;

/**
 * Assess a product.
 *
 * `now` is injected rather than read from the clock so this stays pure and
 * testable.
 */
export function assessQuality(
  product: Omit<Product, 'quality'>,
  now: number = Date.now(),
): DataQuality {
  const per100 = product.nutrition.per_100g ?? product.nutrition.per_100ml ?? null;
  const anyNutrition = Object.values(product.nutrition).some(
    (f) => f != null && hasAnyNutrient(f.nutrients),
  );
  const n = per100?.nutrients ?? null;

  const present: Record<string, boolean> = {
    price: product.price != null && Number.isFinite(product.price.amount),
    quantity: product.quantity != null && product.quantity.netAmount > 0,
    energy: n?.kcal != null || (n?.protein != null && n?.carbohydrate != null && n?.fat != null),
    protein: n?.protein != null,
    carbohydrate: n?.carbohydrate != null,
    fat: n?.fat != null,
    store: product.store != null,
  };

  const missingFields: string[] = [];
  let earned = 0;
  let total = 0;
  for (const field of REQUIRED) {
    total += field.weight;
    if (present[field.key]) earned += field.weight;
    else missingFields.push(field.label);
  }

  const completeness = total === 0 ? 0 : earned / total;

  const warnings: string[] = [];
  if (product.quantity?.source.derived) {
    warnings.push(
      product.quantity.source.note ?? 'Package size was inferred rather than declared.',
    );
  }
  if (per100?.source.derived) {
    warnings.push(per100.source.note ?? 'Nutrition was converted from another basis.');
  }
  if (anyNutrition && per100 === null) {
    warnings.push('Nutrition is available but could not be normalized to a per-100 basis.');
  }

  /*
   * Staleness tracks the PRICE, not `lastUpdated`.
   *
   * `lastUpdated` is the newest timestamp across all of a product's data, so
   * attaching nutrition — which happens on most searches — bumps it to "now"
   * and would mask a price that is genuinely days old. Nutrition does not go
   * stale; a price does, and the price is what the user acts on.
   */
  const priceMs = product.price ? Date.parse(product.price.observedAt) : Number.NaN;
  const fallbackMs = Date.parse(product.lastUpdated);
  const basisMs = Number.isFinite(priceMs) ? priceMs : fallbackMs;

  const ageMs = now - basisMs;
  const stale = Number.isFinite(basisMs) && ageMs > STALENESS_THRESHOLD_MS;
  if (stale) {
    const days = Math.floor(ageMs / (1000 * 60 * 60 * 24));
    warnings.push(
      `This price was last recorded ${days} day${days === 1 ? '' : 's'} ago and may be out of date.`,
    );
  }

  return { grade: gradeFor(completeness, present), completeness, missingFields, warnings, stale };
}

function gradeFor(completeness: number, present: Record<string, boolean>): DataQualityGrade {
  // Without a price there is nothing to compare value against, whatever else
  // we hold.
  if (!present.price) return 'unusable';
  if (completeness >= 0.9) return 'complete';
  if (completeness >= 0.6) return 'partial';
  return 'minimal';
}

export const QUALITY_LABELS: Record<DataQualityGrade, string> = {
  complete: 'Complete',
  partial: 'Partial',
  minimal: 'Minimal',
  unusable: 'Insufficient',
};

export const QUALITY_DESCRIPTIONS: Record<DataQualityGrade, string> = {
  complete: 'Price, package size and full nutrition are available.',
  partial: 'Enough data for most metrics, but some fields are missing.',
  minimal: 'Only basic data is available — most value metrics cannot be computed.',
  unusable: 'Not enough data to compare this product on value.',
};
